/**
 * <video-reel>: a row of portrait clips that scrolls sideways with snap
 * points. The card nearest the middle is "active": it grows, gets the sound
 * button and, once sound is on, keeps it as the shopper steps through. Only
 * the active card plays (muted until sound is on, and only while on screen;
 * not at all with autoplay off or reduced motion, until the sound button):
 * the side cards hold their frame. A tap on a side card brings it to the
 * middle; the arrows step.
 *
 * Without JavaScript the row still scrolls and the posters show.
 */
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class VideoReel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.cards = Array.from(this.querySelectorAll('[data-card]'));
    this.prevButton = this.querySelector('[data-prev]');
    this.nextButton = this.querySelector('[data-next]');
    if (!this.track || this.cards.length === 0) return;

    this.autoplay = this.hasAttribute('data-autoplay') && !REDUCED_MOTION.matches;
    this.soundOn = false;
    this.activeIndex = -1;
    this.scrollFrame = 0;
    this.settling = 0;
    this.settleTimer = 0;
    this.lockedUntil = 0;

    const requested = Number(this.dataset.start);
    const start = Number.isInteger(requested) && requested >= 0 && requested < this.cards.length
      ? requested
      : Math.floor(this.cards.length / 2);

    this.onTrackScroll = this.onTrackScroll.bind(this);
    this.onResize = this.onResize.bind(this);
    this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
    window.addEventListener('resize', this.onResize);

    this.cards.forEach((card, index) => {
      card.addEventListener('click', (event) => {
        if (index !== this.activeIndex) {
          this.goTo(index);
          return;
        }
        // A tap on the active card toggles sound, like its button does.
        if (!event.target.closest('[data-sound]')) this.setSound(!this.soundOn);
      });
      const button = card.querySelector('[data-sound]');
      if (button) {
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          if (index !== this.activeIndex) {
            this.goTo(index);
            this.setSound(true);
          } else {
            this.setSound(!this.soundOn);
          }
        });
      }
    });

    this.prevButton?.addEventListener('click', () => this.goTo(this.activeIndex - 1));
    this.nextButton?.addEventListener('click', () => this.goTo(this.activeIndex + 1));
    this.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.goTo(this.activeIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.goTo(this.activeIndex + 1);
      }
    });

    // Play only what is on screen.
    this.visible = new Set();
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const card = entry.target;
          if (entry.isIntersecting) {
            this.visible.add(card);
            this.playCard(card);
          } else {
            this.visible.delete(card);
            this.pauseCard(card);
          }
        });
      },
      { threshold: 0.3 },
    );
    this.cards.forEach((card) => this.observer.observe(card));

    // Pause the talking card when the tab is hidden; resume when it is back.
    this.onVisibility = () => {
      if (document.hidden) {
        this.cards.forEach((card) => this.pauseCard(card));
      } else {
        this.visible.forEach((card) => this.playCard(card));
      }
    };
    document.addEventListener('visibilitychange', this.onVisibility);

    // Theme editor: selecting a block brings its card to the middle.
    this.addEventListener('shopify:block:select', (event) => {
      const index = this.cards.indexOf(event.target);
      if (index >= 0) this.goTo(index);
    });

    // Centre the starting card once the row has its size.
    requestAnimationFrame(() => {
      this.scrollToCard(start, 'instant');
      this.setActive(start);
    });
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onTrackScroll);
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.observer?.disconnect();
    cancelAnimationFrame(this.scrollFrame);
    clearTimeout(this.settling);
    clearTimeout(this.settleTimer);
  }

  /** @param {HTMLElement} card */
  video(card) {
    return card.querySelector('video');
  }

  /**
   * Step to a card from a tap, click or key: the card becomes active at once,
   * inside the gesture, so a browser that only allows sound from a gesture
   * (Safari) accepts the unmuted play(); the row then glides to it. While it
   * glides, the scroll handler leaves the active card alone, or the cards
   * passing the middle would each take the sound for a moment.
   * @param {number} index
   */
  goTo(index) {
    const clamped = Math.max(0, Math.min(this.cards.length - 1, index));
    this.lockedUntil = performance.now() + 900;
    this.setActive(clamped);
    this.scrollToCard(clamped);
  }

  /**
   * @param {number} index
   * @param {'smooth' | 'instant'} [behavior]
   */
  scrollToCard(index, behavior) {
    const card = this.cards[Math.max(0, Math.min(this.cards.length - 1, index))];
    if (!card) return;
    const left = card.offsetLeft + card.offsetWidth / 2 - this.track.clientWidth / 2;
    this.track.scrollTo({
      left,
      behavior: behavior || (REDUCED_MOTION.matches ? 'instant' : 'smooth'),
    });
  }

  onTrackScroll() {
    // Always re-check once the row has settled, whatever started the scroll.
    clearTimeout(this.settleTimer);
    this.settleTimer = setTimeout(() => this.setActive(this.nearestIndex()), 160);
    if (this.lockedUntil && performance.now() < this.lockedUntil) return;
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = 0;
      this.setActive(this.nearestIndex());
    });
  }

  onResize() {
    clearTimeout(this.settling);
    this.settling = setTimeout(() => {
      if (this.activeIndex >= 0) this.scrollToCard(this.activeIndex, 'instant');
    }, 120);
  }

  nearestIndex() {
    const middle = this.track.scrollLeft + this.track.clientWidth / 2;
    let best = 0;
    let bestDistance = Infinity;
    this.cards.forEach((card, index) => {
      const distance = Math.abs(card.offsetLeft + card.offsetWidth / 2 - middle);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }

  /** @param {number} index */
  setActive(index) {
    if (index === this.activeIndex) return;
    const previous = this.cards[this.activeIndex];
    const next = this.cards[index];
    this.activeIndex = index;

    if (previous) {
      previous.classList.remove('is-active');
      previous.removeAttribute('data-sound-on');
      this.pauseCard(previous);
      const video = this.video(previous);
      if (video) video.muted = true;
      this.updateSoundButton(previous, false);
    }
    next.classList.add('is-active');
    if (this.visible.has(next)) this.playCard(next);
    this.applySound(next);

    if (this.prevButton) this.prevButton.disabled = index === 0;
    if (this.nextButton) this.nextButton.disabled = index === this.cards.length - 1;
  }

  /**
   * Sound follows the active card: one tap turns it on, the arrows keep it.
   * @param {boolean} on
   */
  setSound(on) {
    this.soundOn = on;
    const card = this.cards[this.activeIndex];
    if (card) this.applySound(card);
  }

  /** @param {HTMLElement} card */
  applySound(card) {
    const video = this.video(card);
    if (!video) return;
    if (this.soundOn) {
      video.muted = false;
      card.setAttribute('data-sound-on', '');
      const playing = video.play();
      if (playing && playing.catch) {
        playing.catch((error) => {
          // A pause() while play() was pending (a card leaving the screen)
          // aborts the promise; that is not a refusal, and the card may not
          // even be the active one any more.
          if (error?.name === 'AbortError' || card !== this.cards[this.activeIndex]) return;
          // Sound needs a gesture the browser did not accept: stay muted.
          this.soundOn = false;
          video.muted = true;
          card.removeAttribute('data-sound-on');
          this.updateSoundButton(card, false);
          if (this.autoplay) video.play().catch(() => {});
        });
      }
    } else {
      video.muted = true;
      card.removeAttribute('data-sound-on');
      if (!this.autoplay) video.pause();
    }
    this.updateSoundButton(card, this.soundOn);
  }

  /**
   * @param {HTMLElement} card
   * @param {boolean} on
   */
  updateSoundButton(card, on) {
    const button = card.querySelector('[data-sound]');
    if (!button) return;
    button.setAttribute('aria-pressed', on ? 'true' : 'false');
    const label = on ? button.dataset.labelOff : button.dataset.labelOn;
    if (label) button.setAttribute('aria-label', label);
  }

  /** Plays a card only if it is the active one; the side cards hold still. @param {HTMLElement} card */
  playCard(card) {
    const video = this.video(card);
    if (!video) return;
    if (card !== this.cards[this.activeIndex]) {
      this.pauseCard(card);
      return;
    }
    const withSound = this.soundOn;
    if (!this.autoplay && !withSound) return;
    video.muted = !withSound;
    const playing = video.play();
    if (playing && playing.catch) playing.catch(() => {});
  }

  /** @param {HTMLElement} card */
  pauseCard(card) {
    const video = this.video(card);
    if (video && !video.paused) video.pause();
  }
}

if (!customElements.get('video-reel')) customElements.define('video-reel', VideoReel);

